import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

export interface EnrollmentTransferListParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  projectId?: number;
  fromDate?: string;
  toDate?: string;
}

/**
 * DTO para crear una cesión de cupo (individual o empresarial)
 */
export interface CreateEnrollmentTransferDto {
  // Indica si es cesión empresarial
  isCorporate: boolean;

  // Para inscripciones individuales
  enrollmentId?: number;

  // Para inscripciones empresariales
  corporateEnrollmentParticipantId?: number;
  corporateEnrollmentId?: number;

  // Campos comunes
  fromParticipantId: number;
  toParticipantId: number;
  reason?: string;
}

/**
 * Vista previa de la cesión de cupo
 */
export interface EnrollmentTransferPreview {
  isCorporate: boolean;

  // Identificadores
  enrollmentId?: number;
  corporateEnrollmentParticipantId?: number;
  corporateEnrollmentId?: number;

  // Proyecto
  projectName: string;
  projectPeriod?: string;

  // Datos corporativos
  companyName?: string;
  companyCoveragePercent?: number;

  // Datos financieros
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  totalInstallments: number;
  paidInstallments: number;
  pendingInstallments: number;

  // Estado y participantes
  enrollmentStatus: string;
  currentParticipantName: string;
  newParticipantName: string;
}

export interface EnrollmentTransferValidation {
  isValid: boolean;
  validationErrors: string[];
  transferPreview?: EnrollmentTransferPreview;
}

/**
 * Respuesta de cesión de cupo
 */
export interface EnrollmentTransferResponse {
  id: number;
  isCorporate: boolean;

  // Para individuales
  enrollmentId?: number;

  // Para empresariales
  corporateEnrollmentParticipantId?: number;
  corporateEnrollmentId?: number;
  companyName?: string;

  // Participantes
  fromParticipant: {
    id: number;
    firstName: string;
    lastName: string;
    documentId: string;
    typeDocument: string;
    email: string;
    phone: string;
    fullName: string;
  };
  toParticipant: {
    id: number;
    firstName: string;
    lastName: string;
    documentId: string;
    typeDocument: string;
    email: string;
    phone: string;
    fullName: string;
  };

  // Proyecto
  project: {
    id: number;
    projectId: string;
    name: string;
    period: string;
  };

  // Datos de la transferencia
  transferDate: Date;
  reason?: string;
  transferredByUserEmail?: string;
  totalAmountAtTransfer: number;
  pendingAmountAtTransfer: number;
  pendingInstallmentsAtTransfer: number;

  // Auditoría
  createdAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class EnrollmentTransferService {
  private _baseUrl: string;
  private _controllerName: string = 'EnrollmentTransfer';

  constructor(private _http: HttpClient) {
    this._baseUrl = environment.apiUrl;
  }

  /**
   * Obtener cesiones de cupo paginadas con filtros
   */
  getPaged(params?: EnrollmentTransferListParams): Observable<any> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.pageNumber) httpParams = httpParams.set('pageNumber', params.pageNumber.toString());
      if (params.pageSize) httpParams = httpParams.set('pageSize', params.pageSize.toString());
      if (params.searchTerm) httpParams = httpParams.set('searchTerm', params.searchTerm);
      if (params.projectId) httpParams = httpParams.set('projectId', params.projectId.toString());
      if (params.fromDate) httpParams = httpParams.set('fromDate', params.fromDate);
      if (params.toDate) httpParams = httpParams.set('toDate', params.toDate);
    }

    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}`, { params: httpParams })
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Obtener una cesión de cupo por ID
   */
  getById(id: number): Observable<any> {
    return this._http.get<any>(`${this._baseUrl}/${this._controllerName}/${id}`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Validar si una transferencia es posible
   */
  validateTransfer(dto: CreateEnrollmentTransferDto): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}/validate`, dto)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Crear una nueva cesión de cupo
   */
  createTransfer(payload: CreateEnrollmentTransferDto): Observable<any> {
    return this._http.post<any>(`${this._baseUrl}/${this._controllerName}`, payload)
      .pipe(catchError(this.handleErrors));
  }

  private handleErrors(error: HttpErrorResponse) {
    let errorMessage = 'Algo salió mal; por favor, intente nuevamente más tarde.';
    
    if (error.error instanceof ErrorEvent) {
      console.error('Error del cliente:', error.error.message);
    } else {
      console.error(`Error del servidor: ${error.status}`, error.error);
      
      // Intentar extraer mensaje de error del backend
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error?.errorMessage) {
        errorMessage = error.error.errorMessage;
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      }
    }
    
    return throwError(() => new Error(errorMessage));
  }
}
